// FLEET LIVE's view model (#6778, epic #6552): the ONE place a number on the Command Deck or
// the Topology is read off `GET /v1/fleet/live` (or its `fleet_live` stream, folded on by
// `fleet-live-frames.ts`). Pure: wire in, what the panels draw out.
//
// OMITTED, NEVER SIMULATED (owner ruling V7): each UI number reads exactly one wire field,
// and a field the wire omits renders nothing. So a HUD item exists only when its field was
// sent, "CI pass" does not exist at all (the wire has no source for it, brief.md item
// 48), and an unreported CPU is `null`, which the deck and the topology draw as no bar, no
// ring and no number.

import {
  FLEET_LIVE_STAGES,
  type FleetLiveEventKind,
  type FleetLiveStage,
  type FleetLiveState,
  type FleetLiveWire,
} from '@developerz/domain';
import { t, tPlural } from '../i18n';
import { shortId } from './fleet-machines';
import { clock, count, minutesSince } from './time';

// The row types, read off the WHOLE body's type: the body is what `GET /v1/fleet/live` parses
// (`FleetLiveWireSchema`, `fleet-live-read.ts`), and each stream row is parsed at the door.
type FleetLiveItemWire = FleetLiveWire['items'][number];
type FleetLiveMachineWire = FleetLiveWire['machines']['shown'][number];
type FleetLiveEventWire = FleetLiveWire['events'][number];

/** The most machine lanes and hexes drawn: #6777 serves at most 16, busiest first. */
export const MAX_SHOWN_MACHINES = 16;

/** The newest events the log shows. */
export const LOG_LINES = 14;

/** Which colour a pill, dot or chip takes (`fleet-live-palette.ts` maps each to a token). */
export type LiveTone = 'live' | 'parked' | 'red' | 'shipped' | 'faint';

/** The stage names, full and short (a column under 76 px), each a literal key. */
export const STAGE_LABEL: Readonly<Record<FleetLiveStage, () => string>> = {
  Ask: () => t('fleet.live.stage_ask'),
  Triage: () => t('fleet.live.stage_triage'),
  Plan: () => t('fleet.live.stage_plan'),
  'Picked up': () => t('fleet.live.stage_picked_up'),
  'Pull request': () => t('fleet.live.stage_pull_request'),
  'Fixed until green': () => t('fleet.live.stage_fixed_until_green'),
  Merged: () => t('fleet.live.stage_merged'),
  Shipped: () => t('fleet.live.stage_shipped'),
};

export const STAGE_SHORT: Readonly<Record<FleetLiveStage, () => string>> = {
  Ask: () => t('fleet.live.stage_short_ask'),
  Triage: () => t('fleet.live.stage_short_triage'),
  Plan: () => t('fleet.live.stage_short_plan'),
  'Picked up': () => t('fleet.live.stage_short_picked_up'),
  'Pull request': () => t('fleet.live.stage_short_pull_request'),
  'Fixed until green': () => t('fleet.live.stage_short_fixed_until_green'),
  Merged: () => t('fleet.live.stage_short_merged'),
  Shipped: () => t('fleet.live.stage_short_shipped'),
};

/** The stage an item enters a machine's lane at ("Picked up"). */
export const MACHINE_STAGE = FLEET_LIVE_STAGES.indexOf('Picked up');

/** One HUD number. `key` names the field it reads, for tests and the tick highlight. */
export type HudItem = {
  readonly key: 'in_flight' | 'queued' | 'busy' | 'merged_today' | 'median_cycle';
  readonly label: string;
  readonly value: string;
  readonly title?: string;
};

/** One machine lane (deck) and hexagon (topology). */
export type LiveMachine = {
  readonly id: string;
  /** The name a lane shows: `label` without its trailing 8-char id. */
  readonly name: string;
  /** Name plus 8-char id, never a hostname: the text alternative's. */
  readonly label: string;
  readonly busy: boolean;
  /** Percent, or `null` when the machine reports no CPU reading. */
  readonly cpu: number | null;
  readonly jobs: number;
};

/** One item on the deck, placed. */
export type LiveItem = {
  readonly id: string;
  /** "#12 title", or the title alone when the item has no number. */
  readonly label: string;
  /** "#12", or the title alone when the item has no number (a narrow column's pill). */
  readonly tag: string;
  readonly stage: number;
  readonly state: FleetLiveState;
  /** Index into `machines`, or -1 when the item sits in an intake lane. */
  readonly machine: number;
  /** Which of the three intake lanes, stable per item. */
  readonly intake: number;
  /** The machine holding the item (this account's), whether or not its lane is shown. */
  readonly runnerId: string | null;
  readonly machineLabel: string | null;
  readonly blocker: { readonly id: string; readonly reason: string } | null;
  /** CI fix rounds on the item's PR; `null` when it has none. */
  readonly ciRounds: number | null;
  readonly since: string;
  readonly tone: LiveTone;
};

export type LogLine = {
  /** Stable across patches, so a line already shown does not slide in again. */
  readonly key: string;
  readonly at: string;
  readonly time: string;
  readonly chip: string;
  readonly tone: LiveTone;
  readonly text: string;
};

export type FleetLiveModel = {
  /** "Streaming N machines", or `null` when `machines.online_total` was not sent. */
  readonly streaming: string | null;
  readonly hud: readonly HudItem[];
  /** "Showing the 16 busiest of N machines", only past 16 online. */
  readonly fold: string | null;
  readonly machines: readonly LiveMachine[];
  readonly items: readonly LiveItem[];
  readonly events: readonly LogLine[];
  readonly topology: { readonly queued: number; readonly inCi: number; readonly shipped: number };
};

/** "#12 title" or "title": an absent number reads as the title alone, never "#undefined". */
export function itemLabel(item: { number?: number | undefined; title: string }): string {
  return item.number === undefined
    ? item.title
    : t('fleet.live.item_label', { number: item.number, title: item.title });
}

function itemTag(item: { number?: number | undefined; title: string }): string {
  return item.number === undefined
    ? item.title
    : t('fleet.live.item_number', { number: item.number });
}

/** A lane's name: the label with its trailing " · <8-char id>" removed. */
export function machineName(label: string, runnerId: string): string {
  const id = shortId(runnerId);
  if (!label.endsWith(id)) return label;
  return label
    .slice(0, -id.length)
    .replace(/[\s·-]+$/u, '')
    .trim();
}

function toneOf(item: FleetLiveItemWire): LiveTone {
  if (item.state === 'blocked') return 'red';
  if (item.state === 'parked') return 'parked';
  if (item.state === 'set_aside') return 'faint';
  return item.stage === 'Merged' || item.stage === 'Shipped' ? 'shipped' : 'live';
}

/** A stable hash of an id: an intake lane and a topology phase that survive patches. */
export function idHash(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h;
}

function machineOf(m: FleetLiveMachineWire): LiveMachine {
  return {
    id: m.runner_id,
    name: machineName(m.label, m.runner_id),
    label: m.label,
    busy: m.jobs > 0,
    cpu: m.cpu_pct ?? null,
    jobs: m.jobs,
  };
}

function itemOf(item: FleetLiveItemWire, machineIndex: ReadonlyMap<string, number>): LiveItem {
  const stage = FLEET_LIVE_STAGES.indexOf(item.stage);
  const onMachine = item.runner_id === undefined ? -1 : (machineIndex.get(item.runner_id) ?? -1);
  return {
    id: item.id,
    label: itemLabel(item),
    tag: itemTag(item),
    stage,
    state: item.state,
    machine: stage >= MACHINE_STAGE ? onMachine : -1,
    intake: idHash(item.id) % 3,
    runnerId: item.runner_id ?? null,
    machineLabel: item.machine_label ?? null,
    blocker: item.blocker ?? null,
    ciRounds: item.ci_fix_rounds ?? null,
    since: item.since,
    tone: toneOf(item),
  };
}

function hudOf(wire: FleetLiveWire): HudItem[] {
  const hud: HudItem[] = [
    { key: 'in_flight', label: t('fleet.live.hud_in_flight'), value: count(wire.counts.in_flight) },
    { key: 'queued', label: t('fleet.live.hud_queued'), value: count(wire.counts.queued) },
  ];
  const { busy, online_total: online } = wire.machines;
  if (busy !== undefined && online !== undefined) {
    hud.push({ key: 'busy', label: t('fleet.live.hud_busy'), value: `${busy}/${online}` });
  }
  hud.push({
    key: 'merged_today',
    label: t('fleet.live.hud_merged_today'),
    value: count(wire.merged_today.count),
    title: t('fleet.live.hud_merged_since'),
  });
  if (wire.median_cycle_minutes !== undefined) {
    hud.push({
      key: 'median_cycle',
      label: t('fleet.live.hud_median_cycle'),
      value: t('fleet.live.minutes', { count: count(Math.round(wire.median_cycle_minutes)) }),
    });
  }
  return hud;
}

type EventFacts = { label: string; machine?: string; blocker?: { id: string; reason: string } };

/** Each kind's sentence. A kind with no entry has no sentence (the record is total, so none). */
const SENTENCE: Readonly<
  Record<FleetLiveEventKind, (e: EventFacts, ciTry: number | null) => string>
> = {
  ask: (e) => t('fleet.live.event_ask', { label: e.label }),
  aside: (e) => t('fleet.live.event_aside', { label: e.label }),
  pick: (e) =>
    e.machine === undefined
      ? t('fleet.live.event_pick_bare', { label: e.label })
      : t('fleet.live.event_pick', { label: e.label, machine: e.machine }),
  pr: (e) => t('fleet.live.event_pr', { label: e.label }),
  ci: (e, ciTry) => ciSentence(e, ciTry),
  blocked: (e) =>
    e.blocker === undefined
      ? t('fleet.live.event_blocked_bare', { label: e.label })
      : t('fleet.live.event_blocked', { label: e.label, ...e.blocker }),
  parked: (e) =>
    e.blocker === undefined
      ? t('fleet.live.event_parked_bare', { label: e.label })
      : t('fleet.live.event_parked', { label: e.label, ...e.blocker }),
  // brief.md item 53: the event carries no parked/blocked discriminator, so one sentence.
  resolved: (e) => t('fleet.live.event_resolved', { label: e.label }),
  merged: (e) => t('fleet.live.event_merged', { label: e.label }),
  shipped: (e) => t('fleet.live.event_shipped', { label: e.label }),
};

function ciSentence(e: EventFacts, ciTry: number | null): string {
  if (e.machine === undefined) {
    return ciTry === null
      ? t('fleet.live.event_ci_bare', { label: e.label })
      : t('fleet.live.event_ci_try', { label: e.label, try: ciTry });
  }
  return ciTry === null
    ? t('fleet.live.event_ci_machine', { label: e.label, machine: e.machine })
    : t('fleet.live.event_ci', { label: e.label, machine: e.machine, try: ciTry });
}

/** Each kind's chip: its word and its tone. */
export const CHIP: Readonly<Record<FleetLiveEventKind, { label: () => string; tone: LiveTone }>> = {
  ask: { label: () => t('fleet.live.chip_ask'), tone: 'faint' },
  pick: { label: () => t('fleet.live.chip_pick'), tone: 'live' },
  pr: { label: () => t('fleet.live.chip_pr'), tone: 'live' },
  ci: { label: () => t('fleet.live.chip_ci'), tone: 'red' },
  blocked: { label: () => t('fleet.live.chip_blocked'), tone: 'red' },
  parked: { label: () => t('fleet.live.chip_parked'), tone: 'parked' },
  resolved: { label: () => t('fleet.live.chip_resolved'), tone: 'shipped' },
  merged: { label: () => t('fleet.live.chip_merged'), tone: 'shipped' },
  shipped: { label: () => t('fleet.live.chip_shipped'), tone: 'shipped' },
  aside: { label: () => t('fleet.live.chip_aside'), tone: 'faint' },
};

/**
 * The log's lines, newest first. A `ci` event's "(try n)" reads its item's
 * `ci_fix_rounds` + 1 when that item is among `items[]`, else the parenthesis is omitted.
 */
export function logLines(
  events: readonly FleetLiveEventWire[],
  items: readonly FleetLiveItemWire[],
): LogLine[] {
  const rounds = new Map(items.map((i) => [i.id, i.ci_fix_rounds]));
  const seen = new Map<string, number>();
  return events.slice(0, LOG_LINES).map((e) => {
    const base = `${e.at}|${e.kind}|${e.item.id}`;
    const n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    const r = rounds.get(e.item.id);
    const facts: EventFacts = {
      label: itemLabel(e.item),
      ...(e.machine_label === undefined ? {} : { machine: e.machine_label }),
      ...(e.blocker === undefined ? {} : { blocker: e.blocker }),
    };
    return {
      key: n === 0 ? base : `${base}|${n}`,
      at: e.at,
      time: clock(e.at),
      chip: CHIP[e.kind].label(),
      tone: CHIP[e.kind].tone,
      text: SENTENCE[e.kind](facts, r === undefined ? null : r + 1),
    };
  });
}

function streamingOf(online: number | undefined): string | null {
  if (online === undefined) return null;
  return tPlural('fleet.live.streaming', online, { count: count(online) });
}

function foldOf(online: number | undefined, shown: number): string | null {
  if (online === undefined || online <= MAX_SHOWN_MACHINES) return null;
  return t('fleet.live.fold', { shown, count: count(online) });
}

/** The whole model the section draws, off one wire body. */
export function fleetLiveModel(wire: FleetLiveWire): FleetLiveModel {
  const shown = wire.machines.shown.slice(0, MAX_SHOWN_MACHINES);
  const machines = shown.map(machineOf);
  const index = new Map(machines.map((m, i) => [m.id, i]));
  return {
    streaming: streamingOf(wire.machines.online_total),
    hud: hudOf(wire),
    fold: foldOf(wire.machines.online_total, machines.length),
    machines,
    items: wire.items.map((i) => itemOf(i, index)),
    events: logLines(wire.events, wire.items),
    topology: {
      queued: wire.counts.queued,
      inCi: wire.counts.in_ci,
      shipped: wire.shipped_today.count,
    },
  };
}

/** An item's state as a sentence: the tooltip's second line and the text alternative's. */
export function itemStateText(item: LiveItem): string {
  const id = item.blocker?.id ?? '';
  switch (item.state) {
    case 'blocked':
      return t('fleet.live.tip_blocked', { id });
    case 'parked':
      return t('fleet.live.tip_parked', { id });
    case 'set_aside':
      return t('fleet.live.tip_set_aside');
    case 'waiting':
      return t('fleet.live.tip_waiting');
    case 'running': {
      const stage = FLEET_LIVE_STAGES[item.stage];
      return stage === undefined ? '' : STAGE_LABEL[stage]();
    }
  }
}

/** Where an item is: the machine (or "Not on a machine yet"). */
export function itemMachineText(item: LiveItem): string {
  return item.machineLabel ?? t('fleet.live.tip_no_machine');
}

/**
 * The tooltip's third line: "<machine> · CI tries <rounds + 1> · <minutes> min in flight",
 * the CI part omitted when the item has no PR (no `ci_fix_rounds`). Minutes off `since`
 * through `lib/time.ts`.
 */
export function itemWhereText(item: LiveItem, now: number): string {
  const machine = itemMachineText(item);
  const minutes = count(minutesSince(item.since, now) ?? 0);
  return item.ciRounds === null
    ? t('fleet.live.tip_where_no_ci', { machine, minutes })
    : t('fleet.live.tip_where', { machine, tries: item.ciRounds + 1, minutes });
}
